import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  offlineRequestsKeys,
  useNetworkStatus,
} from './queries/useOfflineRequestsQuery';
import { requestsKeys } from './queries/useRequestsQuery';
import { offlineCache } from '../services/offlineCache';
import { RequestsService } from '../services/requestsService';
import { Request } from '../types';

import type { OfflineRequestDetailCache } from './queries/useOfflineRequestsQuery';

interface RejectRequestParams {
  requestId: string;
  reason: string;
}

export const useRejectRequest = () => {
  const queryClient = useQueryClient();
  const isOnline = useNetworkStatus();

  return useMutation({
    mutationFn: async ({ requestId, reason }: RejectRequestParams) => {
      if (!isOnline) {
        throw new Error(
          'Cannot reject requests while offline. Please connect to internet.',
        );
      }
      return RequestsService.rejectRequest(requestId, reason);
    },
    onSuccess: async (result, variables) => {
      const mergedResult: OfflineRequestDetailCache = {
        ...result,
        latestRejectionReason:
          result.latestRejectionReason ?? variables.reason.trim(),
        latestRejectionRejectedByEmail: result.latestRejectionRejectedByEmail,
        _offlineDetailNeedsHydration: false,
      };
      const requestDetailKey = requestsKeys.detail(mergedResult.id);
      const offlineDetailKey = offlineRequestsKeys.detail(mergedResult.id);
      const mergeDetail = (oldData: Request | undefined) =>
        oldData ? { ...oldData, ...mergedResult } : mergedResult;
      const mergeInList = (oldData: Request[] | undefined) =>
        oldData?.map(request =>
          request.id === mergedResult.id
            ? { ...request, ...mergedResult }
            : request,
        );

      queryClient.setQueryData(requestDetailKey, mergeDetail);
      queryClient.setQueryData(offlineDetailKey, mergeDetail);
      queryClient.setQueryData(requestsKeys.lists(), mergeInList);
      queryClient.setQueryData(offlineRequestsKeys.lists(), mergeInList);

      try {
        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = requests.map(request =>
          request.id === mergedResult.id
            ? { ...request, ...mergedResult }
            : request,
        );
        await offlineCache.cacheRequests(updatedRequests);
      } catch (error) {
        console.error('Failed to update offline cache after reject:', error);
      }

      queryClient.invalidateQueries({
        queryKey: requestsKeys.all,
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.detail(mergedResult.id),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.lists(),
        refetchType: 'active',
      });
    },
    meta: {
      errorMessage: 'Failed to reject request. Please try again.',
    },
  });
};
